import type { BeadTechnique } from '@bransoletki/shared';
import { BEAD_ASPECT_RATIO } from '@bransoletki/shared';

/**
 * Screen position of a single bead on the canvas.
 */
export interface BeadPosition {
  readonly row: number;
  readonly col: number;
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

/**
 * Configuration for grid rendering.
 */
export interface GridRenderConfig {
  readonly technique: BeadTechnique;
  readonly gridWidth: number;
  readonly gridHeight: number;
  /** Base size of a bead in pixels (width). Height = width * ASPECT_RATIO */
  readonly cellSize: number;
  /** Gap between beads in pixels */
  readonly gap: number;
}

const DEFAULT_CELL_SIZE = 20;
const DEFAULT_GAP = 1;

/**
 * Calculate pixel positions for all beads in a grid.
 * Pure function — no side effects, easy to test.
 */
export function calculateBeadPositions(config: GridRenderConfig): readonly BeadPosition[] {
  switch (config.technique) {
    case 'loom':
      return calculateLoomPositions(config);
    case 'peyote':
      return calculatePeyotePositions(config);
    case 'brick':
      return calculateBrickPositions(config);
  }
}

/**
 * Calculate total canvas size needed for the grid.
 */
export function calculateGridSize(config: GridRenderConfig): { width: number; height: number } {
  const beadW = config.cellSize;
  const beadH = config.cellSize * BEAD_ASPECT_RATIO;
  const gap = config.gap;

  switch (config.technique) {
    case 'loom':
      return {
        width: config.gridWidth * (beadW + gap) - gap,
        height: config.gridHeight * (beadH + gap) - gap,
      };

    case 'peyote':
      return {
        width: config.gridWidth * (beadW + gap) - gap,
        height: config.gridHeight * (beadH / 2 + gap) + beadH / 2,
      };

    case 'brick':
      return {
        width: config.gridWidth * (beadW / 2 + gap) + beadW / 2,
        height: config.gridHeight * (beadH + gap) - gap,
      };
  }
}

/**
 * Create a default render config.
 */
export function createRenderConfig(
  technique: BeadTechnique,
  gridWidth: number,
  gridHeight: number,
  cellSize = DEFAULT_CELL_SIZE,
  gap = DEFAULT_GAP,
): GridRenderConfig {
  return { technique, gridWidth, gridHeight, cellSize, gap };
}

/**
 * Hit test: find which bead (row, col) is at pixel coordinates.
 * Returns null if no bead at that position.
 */
export function hitTestBead(
  x: number,
  y: number,
  positions: readonly BeadPosition[],
): BeadPosition | null {
  for (const pos of positions) {
    if (x >= pos.x && x <= pos.x + pos.width && y >= pos.y && y <= pos.y + pos.height) {
      return pos;
    }
  }
  return null;
}

// ─── Loom ───────────────────────────────────────────────

function calculateLoomPositions(config: GridRenderConfig): BeadPosition[] {
  const { gridWidth, gridHeight, cellSize, gap } = config;
  const beadW = cellSize;
  const beadH = cellSize * BEAD_ASPECT_RATIO;
  const positions: BeadPosition[] = [];

  for (let row = 0; row < gridHeight; row++) {
    for (let col = 0; col < gridWidth; col++) {
      positions.push({
        row,
        col,
        x: col * (beadW + gap),
        y: row * (beadH + gap),
        width: beadW,
        height: beadH,
      });
    }
  }

  return positions;
}

// ─── Peyote ─────────────────────────────────────────────
// Even rows (0, 2, 4...) are flush left.
// Odd rows (1, 3, 5...) are offset right by half a bead width.
// Each row is offset vertically by half a bead height (beads interlock).

function calculatePeyotePositions(config: GridRenderConfig): BeadPosition[] {
  const { gridWidth, gridHeight, cellSize, gap } = config;
  const beadW = cellSize;
  const beadH = cellSize * BEAD_ASPECT_RATIO;
  const positions: BeadPosition[] = [];

  for (let row = 0; row < gridHeight; row++) {
    const isOddRow = row % 2 === 1;
    const offsetX = isOddRow ? (beadW + gap) / 2 : 0;

    for (let col = 0; col < gridWidth; col++) {
      positions.push({
        row,
        col,
        x: col * (beadW + gap) + offsetX,
        y: row * (beadH / 2 + gap),
        width: beadW,
        height: beadH,
      });
    }
  }

  return positions;
}

// ─── Brick ──────────────────────────────────────────────
// Like Peyote rotated 90°. Beads are laid horizontally.
// Even rows flush left, odd rows offset down by half bead height.
// Each column is offset by half a bead width.

function calculateBrickPositions(config: GridRenderConfig): BeadPosition[] {
  const { gridWidth, gridHeight, cellSize, gap } = config;
  const beadW = cellSize;
  const beadH = cellSize * BEAD_ASPECT_RATIO;
  const positions: BeadPosition[] = [];

  for (let row = 0; row < gridHeight; row++) {
    const isOddRow = row % 2 === 1;
    const offsetX = isOddRow ? (beadW + gap) / 2 : 0;

    for (let col = 0; col < gridWidth; col++) {
      positions.push({
        row,
        col,
        x: col * (beadW + gap) + offsetX,
        y: row * (beadH + gap),
        width: beadW,
        height: beadH,
      });
    }
  }

  return positions;
}
